import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Menu, MenuItem,
  Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import { AddOutlined, ContentCopyOutlined, DeleteOutlined, FileDownloadOutlined } from "@mui/icons-material";
import { toast } from "react-toastify";
import { api } from "../../services/api";
import FilasSkeleton from "../ui/FilasSkeleton";
import { BORDE, Campo, estiloSwitch, FUENTE, PRIMARIO, TEXTO_SECUNDARIO, TITULO } from "../ui/Formulario";
import { linkDeCanal, normalizarCodigo } from "../../domain/atribucion/atribucion";

const PARAMETROS_META = "utm_source=facebook&utm_medium=paid&utm_campaign={{campaign.name}}";

const pesos = (n) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n || 0);
const porcentaje = (n) => `${(n || 0).toLocaleString("es-AR", { maximumFractionDigits: 1 })} %`;

const encabezadoSx = {
  fontSize: 12,
  fontWeight: 500,
  color: TEXTO_SECUNDARIO,
  bgcolor: "#fafafa",
  borderBottom: `1px solid ${BORDE}`,
  whiteSpace: "nowrap",
  py: 1.25,
};
const numeroSx = { textAlign: "right", whiteSpace: "nowrap" };
const tablaSx = {
  "& .MuiTableCell-body": { fontSize: 14, color: TITULO, borderBottom: `1px solid ${BORDE}`, py: 1.5 },
  "& .MuiTableRow-root:last-of-type .MuiTableCell-body": { borderBottom: 0 },
};
const botonSx = { textTransform: "none", borderRadius: "8px" };

const Seccion = ({ titulo, ayuda, accion, children }) => (
  <Box sx={{ border: `1px solid ${BORDE}`, borderRadius: "12px", bgcolor: "#fff", mb: 3, overflow: "hidden" }}>
    <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, px: { xs: 2, sm: 3 }, py: 2.5 }}>
      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography sx={{ fontFamily: FUENTE, fontWeight: 700, fontSize: 20, color: TITULO }}>{titulo}</Typography>
        {ayuda && <Typography sx={{ fontSize: 13, color: TEXTO_SECUNDARIO, mt: 0.5 }}>{ayuda}</Typography>}
      </Box>
      {accion}
    </Box>
    <TableContainer>{children}</TableContainer>
  </Box>
);

const Codigo = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: "monospace",
      fontSize: 12,
      px: 0.75,
      py: 0.25,
      borderRadius: "6px",
      color: PRIMARIO,
      bgcolor: "rgba(86, 86, 255, 0.08)",
    }}
  >
    {children}
  </Box>
);

const NuevoCanal = ({ open, onClose, onCrear }) => {
  const [nombre, setNombre] = useState("");
  const [codigo, setCodigo] = useState("");
  const [codigoTocado, setCodigoTocado] = useState(false);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (open) {
      setNombre("");
      setCodigo("");
      setCodigoTocado(false);
    }
  }, [open]);

  const guardar = async () => {
    setGuardando(true);
    const ok = await onCrear({ nombre: nombre.trim(), codigo });
    setGuardando(false);
    if (ok) onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
      <DialogTitle sx={{ fontFamily: FUENTE, fontWeight: 700, color: TITULO }}>Nuevo canal</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "4px !important" }}>
        <Campo
          etiqueta="Nombre"
          placeholder="Ej.: Full Dance, Academia Sol, Meta Ads"
          autoFocus
          value={nombre}
          inputProps={{ maxLength: 100 }}
          onChange={(e) => {
            setNombre(e.target.value);
            if (!codigoTocado) setCodigo(normalizarCodigo(e.target.value));
          }}
        />
        <Campo
          etiqueta="Código"
          placeholder="Ej.: FULLDANCE"
          value={codigo}
          onChange={(e) => {
            setCodigoTocado(true);
            setCodigo(normalizarCodigo(e.target.value));
          }}
          ayuda="Va en el link y es lo que escribe el comprador. Después no se puede cambiar."
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ ...botonSx, color: "#374151" }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={guardar}
          disabled={guardando || !nombre.trim() || codigo.length < 2}
          sx={{ ...botonSx, bgcolor: PRIMARIO }}
        >
          {guardando && <CircularProgress size={16} sx={{ mr: 1, color: "white" }} />}
          Crear canal
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const GestionCanales = ({ eventoId, rutaEvento, puedeEditar }) => {
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [canales, setCanales] = useState(null);
  const [vigentes, setVigentes] = useState({});
  const [campanias, setCampanias] = useState(null);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);
  const [menuExcel, setMenuExcel] = useState(null);
  const [borrando, setBorrando] = useState(null);

  const cargarCanales = useCallback(async () => {
    try {
      const [reporte, lista] = await Promise.all([
        api.canales.reporte(eventoId, "canales", { desde, hasta }),
        api.canales.listar(eventoId),
      ]);
      if (reporte.status !== "success") throw new Error(reporte.message);
      setCanales(reporte.data);
      setVigentes(Object.fromEntries((lista.data || []).map((c) => [c.id, c])));
    } catch (e) {
      setCanales([]);
      toast.error(e.message || "No se pudieron cargar los canales.");
    }
  }, [eventoId, desde, hasta]);

  const cargarCampanias = useCallback(async () => {
    try {
      const reporte = await api.canales.reporte(eventoId, "campanias", { desde, hasta });
      if (reporte.status !== "success") throw new Error(reporte.message);
      setCampanias(reporte.data);
    } catch (e) {
      setCampanias([]);
    }
  }, [eventoId, desde, hasta]);

  useEffect(() => {
    setCanales(null);
    setCampanias(null);
    cargarCanales();
    cargarCampanias();
  }, [cargarCanales, cargarCampanias]);

  const crear = async (canal) => {
    const r = await api.canales.crear(eventoId, canal);
    if (r.status !== "success") {
      toast.error(r.message || "No se pudo crear el canal.");
      return false;
    }
    toast.success("Canal creado.");
    cargarCanales();
    return true;
  };

  const cambiarActivo = async (idcanal, activo) => {
    setVigentes((v) => ({ ...v, [idcanal]: { ...v[idcanal], activo } }));
    const r = await api.canales.editar(eventoId, idcanal, { activo });
    if (r.status !== "success") toast.error(r.message || "No se pudo guardar.");
    cargarCanales();
  };

  const borrar = async () => {
    const r = await api.canales.borrar(eventoId, borrando.idcanal);
    if (r.status !== "success") toast.error(r.message || "No se pudo borrar.");
    setBorrando(null);
    cargarCanales();
  };

  const copiar = async (codigo) => {
    try {
      await navigator.clipboard.writeText(linkDeCanal(window.location.origin, rutaEvento, codigo));
      toast.success("Link copiado.");
    } catch {
      toast.error("No se pudo copiar el link.");
    }
  };

  const copiarParametros = async () => {
    try {
      await navigator.clipboard.writeText(PARAMETROS_META);
      toast.success("Parámetros copiados.");
    } catch {
      toast.error("No se pudieron copiar.");
    }
  };

  const descargar = async (reporte) => {
    setMenuExcel(null);
    try {
      const archivo = await api.canales.reporte(eventoId, reporte, { desde, hasta, formato: "xlsx" });
      const url = URL.createObjectURL(archivo);
      const a = document.createElement("a");
      a.href = url;
      a.download = reporte === "canales" ? `ventas-por-canal-${eventoId}.xlsx` : `ventas-por-campania-${eventoId}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el reporte.");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 1.5, mb: 3 }}>
        <Campo etiqueta="Desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} sx={{ width: 170 }} />
        <Campo etiqueta="Hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} sx={{ width: 170 }} />
        <Box sx={{ flex: 1 }} />
        <Button
          variant="outlined"
          startIcon={<FileDownloadOutlined />}
          onClick={(e) => setMenuExcel(e.currentTarget)}
          sx={{ ...botonSx, height: 40, color: TITULO, borderColor: "#d4d4d8" }}
        >
          Descargar Excel
        </Button>
        <Menu anchorEl={menuExcel} open={Boolean(menuExcel)} onClose={() => setMenuExcel(null)}>
          <MenuItem onClick={() => descargar("canales")}>Ventas por canal</MenuItem>
          <MenuItem onClick={() => descargar("campanias")}>Ventas por campaña</MenuItem>
        </Menu>
      </Box>

      <Seccion
        titulo="Por canal"
        ayuda="Compras aprobadas. El importe no incluye el costo del servicio."
        accion={
          puedeEditar && (
            <Button variant="contained" disableElevation startIcon={<AddOutlined />} onClick={() => setNuevoAbierto(true)} sx={{ ...botonSx, bgcolor: PRIMARIO, flexShrink: 0 }}>
              Nuevo canal
            </Button>
          )
        }
      >
        <Table size="small" sx={tablaSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoSx}>Canal</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Visitas</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Compras</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Entradas</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Importe</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Conversión</TableCell>
              <TableCell sx={encabezadoSx}>Activo</TableCell>
              <TableCell sx={encabezadoSx} />
            </TableRow>
          </TableHead>
          <TableBody>
            {canales === null ? (
              <FilasSkeleton columnas={8} filas={3} />
            ) : (
              canales.map((f) => {
                const vigente = f.idcanal ? vigentes[f.idcanal] : null;
                return (
                  <TableRow key={f.idcanal ?? "sin-canal"} sx={{ opacity: f.idcanal && !vigente ? 0.55 : 1 }}>
                    <TableCell>
                      <Typography sx={{ fontSize: 14, fontWeight: 500, color: TITULO }}>{f.nombre}</Typography>
                      {f.codigo && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
                          <Codigo>{f.codigo}</Codigo>
                          {vigente && (
                            <Tooltip title="Copiar link">
                              <IconButton size="small" onClick={() => copiar(f.codigo)} aria-label="Copiar link" sx={{ color: TEXTO_SECUNDARIO }}>
                                <ContentCopyOutlined sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {!vigente && <Typography sx={{ fontSize: 12, color: TEXTO_SECUNDARIO }}>borrado</Typography>}
                        </Box>
                      )}
                      {!f.idcanal && (
                        <Typography sx={{ fontSize: 12, color: TEXTO_SECUNDARIO, mt: 0.25 }}>Sin link ni código</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={numeroSx}>{f.idcanal ? f.visitas : "—"}</TableCell>
                    <TableCell sx={numeroSx}>{f.compras}</TableCell>
                    <TableCell sx={numeroSx}>{f.entradas}</TableCell>
                    <TableCell sx={numeroSx}>{pesos(f.importe)}</TableCell>
                    <TableCell sx={numeroSx}>{f.idcanal && f.visitas > 0 ? porcentaje(f.conversion) : "—"}</TableCell>
                    <TableCell>
                      {vigente && (
                        <Switch
                          size="small"
                          sx={estiloSwitch}
                          checked={vigente.activo}
                          disabled={!puedeEditar}
                          onChange={(e) => cambiarActivo(f.idcanal, e.target.checked)}
                          inputProps={{ "aria-label": `Activar ${f.nombre}` }}
                        />
                      )}
                    </TableCell>
                    <TableCell sx={{ width: 48 }}>
                      {vigente && puedeEditar && (
                        <Tooltip title="Borrar canal">
                          <IconButton size="small" onClick={() => setBorrando(f)} aria-label={`Borrar ${f.nombre}`} sx={{ color: TEXTO_SECUNDARIO }}>
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Seccion>

      <Seccion
        titulo="Por campaña de anuncios"
        ayuda="Agrupa las compras por los parámetros UTM del link del anuncio."
        accion={
          <Tooltip title="Pegalos en «Parámetros de URL» de tu anuncio en Meta Ads">
            <Button startIcon={<ContentCopyOutlined sx={{ fontSize: "16px !important" }} />} onClick={copiarParametros} sx={{ ...botonSx, color: PRIMARIO, flexShrink: 0 }}>
              Copiar parámetros para Meta Ads
            </Button>
          </Tooltip>
        }
      >
        <Table size="small" sx={tablaSx}>
          <TableHead>
            <TableRow>
              <TableCell sx={encabezadoSx}>Origen</TableCell>
              <TableCell sx={encabezadoSx}>Medio</TableCell>
              <TableCell sx={encabezadoSx}>Campaña</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Compras</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Entradas</TableCell>
              <TableCell sx={{ ...encabezadoSx, ...numeroSx }}>Importe</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {campanias === null ? (
              <FilasSkeleton columnas={6} filas={3} />
            ) : campanias.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ color: `${TEXTO_SECUNDARIO} !important`, textAlign: "center", py: "32px !important" }}>
                  Todavía no hay compras en este período.
                </TableCell>
              </TableRow>
            ) : (
              campanias.map((f, i) => {
                const sinUTM = !f.utm_source && !f.utm_medium && !f.utm_campaign;
                return (
                  <TableRow key={i}>
                    <TableCell colSpan={sinUTM ? 3 : 1} sx={sinUTM ? { color: `${TEXTO_SECUNDARIO} !important` } : {}}>
                      {sinUTM ? "Sin parámetros UTM" : f.utm_source || "—"}
                    </TableCell>
                    {!sinUTM && <TableCell>{f.utm_medium || "—"}</TableCell>}
                    {!sinUTM && <TableCell>{f.utm_campaign || "—"}</TableCell>}
                    <TableCell sx={numeroSx}>{f.compras}</TableCell>
                    <TableCell sx={numeroSx}>{f.entradas}</TableCell>
                    <TableCell sx={numeroSx}>{pesos(f.importe)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </Seccion>

      <NuevoCanal open={nuevoAbierto} onClose={() => setNuevoAbierto(false)} onCrear={crear} />

      <Dialog open={Boolean(borrando)} onClose={() => setBorrando(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: "12px" } }}>
        <DialogTitle sx={{ fontFamily: FUENTE, fontWeight: 700, color: TITULO }}>¿Borrar {borrando?.nombre}?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: TEXTO_SECUNDARIO }}>
            El link y el código dejan de sumar ventas a este canal. Lo que ya vendió sigue en los reportes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setBorrando(null)} sx={{ ...botonSx, color: "#374151" }}>
            Cancelar
          </Button>
          <Button variant="contained" disableElevation color="error" onClick={borrar} sx={botonSx}>
            Borrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GestionCanales;
